import { getContactsSortedByRank } from '@/services/contact-point';
import { normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { Button, Checkbox, Flex, Select, Stack, Text, TextInput } from '@mantine/core';
import { Patient } from '@medplum/fhirtypes';
import { useState, useCallback, useEffect } from 'react';
import { useTimelineContext } from '../../hooks/useTimelineContext';
import { InfoCard } from '../shared/InfoCard';
import { UnifiedContactList } from './UnifiedContactList';

type ContactLabel = 'Mobile' | 'Home' | 'Work';
type ContactSystem = 'phone' | 'email';

interface ContactItem {
  value: string;
  label?: ContactLabel;
  primary?: boolean;
  system: ContactSystem;
  id?: string;
}

interface EditingContact {
  id: string;
  value: string;
  label: ContactLabel;
  system: ContactSystem;
  primary: boolean;
}

function getUUID() {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : Math.random().toString(36).substring(2, 15);
}

interface AddContactPointFormProps {
  onAdd: (item: ContactItem) => void;
  onClose: () => void;
  initialValue?: string;
  initialLabel?: ContactLabel;
  initialSystem?: ContactSystem;
  initialPrimary?: boolean;
  editingId?: string;
}

function AddContactPointForm({
  onAdd,
  onClose,
  initialValue,
  initialLabel,
  initialSystem,
  initialPrimary,
  editingId,
}: AddContactPointFormProps) {
  const [value, setValue] = useState(initialValue || '');
  const [label, setLabel] = useState<ContactLabel>(initialLabel || 'Mobile');
  const [primary, setPrimary] = useState(initialPrimary || false);
  const [system, setSystem] = useState<ContactSystem>(initialSystem || 'phone');

  // Update form when initial values change (for edit mode)
  useEffect(() => {
    if (initialValue !== undefined) {
      setValue(initialValue);
    }
    if (initialLabel !== undefined) {
      setLabel(initialLabel || 'Mobile');
    }
    if (initialSystem !== undefined) {
      setSystem(initialSystem);
    }
    if (initialPrimary !== undefined) {
      setPrimary(initialPrimary);
    }
  }, [initialValue, initialLabel, initialSystem, initialPrimary]);

  // Infer system from value
  const inferredSystem =
    value.trim().startsWith('+') || /^\d/.test(value.trim()) ? 'phone' : value.includes('@') ? 'email' : system;

  const handleAdd = () => {
    if (!value || value.trim().length < 2) return;
    const finalSystem = inferredSystem;
    onAdd({ value: value.trim(), label, primary, system: finalSystem, id: editingId });
    setValue('');
    setLabel('Mobile');
    setPrimary(false);
    onClose();
  };

  return (
    <Stack gap={8} mb={8}>
      <TextInput
        label="Enter phone or email"
        placeholder="Enter phone or email"
        value={value}
        onChange={(e) => {
          setValue(e.currentTarget.value);
          const newValue = e.currentTarget.value.trim();
          if (newValue.startsWith('+') || /^\d/.test(newValue)) {
            setSystem('phone');
          } else if (newValue.includes('@')) {
            setSystem('email');
          }
        }}
        radius="sm"
        size="sm"
      />
      <Select
        label="Contact label"
        placeholder="Select label"
        value={label}
        onChange={(value) => setLabel((value as ContactLabel) || 'Mobile')}
        data={['Mobile', 'Home', 'Work'] as ContactLabel[]}
        radius="sm"
        size="sm"
      />
      <Checkbox
        label={
          <Text size="sm" fw={400}>
            Set as primary
          </Text>
        }
        checked={primary}
        onChange={(e) => setPrimary(e.currentTarget.checked)}
        size="sm"
        radius="sm"
        color="blue.6"
      />
      <Flex justify="flex-end" gap={8}>
        <Button variant="subtle" color="blue.6" radius="sm" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button color="blue.6" radius="xl" size="sm" onClick={handleAdd} disabled={!value || value.trim().length < 2}>
          Save
        </Button>
      </Flex>
    </Stack>
  );
}

type ContactCardProps = {
  patient: Patient;
};

export function ContactCard({ patient }: ContactCardProps) {
  const { patientContext } = useTimelineContext();
  const updatePatient = patientContext?.updatePatient;
  const items = getContactsSortedByRank(patient.telecom || [], undefined);
  const [editingContact, setEditingContact] = useState<EditingContact | null>(null);
  const [showForm, setShowForm] = useState(false);

  const handleDeleteItem = useCallback(
    async (id: string) => {
      if (!updatePatient) return;
      const telecom = (patient.telecom || []).filter((cp) => cp.id !== id);
      await updatePatient({ telecom });
    },
    [patient.telecom, updatePatient]
  );

  const handleAddItem = useCallback(
    async (item: ContactItem) => {
      if (!updatePatient) return;
      const labelToUse = (rawLabel?: ContactLabel) => rawLabel?.toLowerCase() as 'mobile' | 'home' | 'work' | undefined;

      // If editing (id provided), update existing contact
      if (item.id) {
        const telecom = (patient.telecom || []).map((cp) => {
          if (cp.id !== item.id) return cp;
          let value = item.value;
          if (item.system === 'phone') {
            value = normalizePhoneNumber(value);
          }
          return {
            ...cp,
            value,
            system: item.system,
            use: labelToUse(item.label) ?? cp.use,
            rank: item.primary ? 1 : cp.rank,
          };
        });

        // If setting as primary, update other contacts of the same system
        if (item.primary) {
          for (let i = 0; i < telecom.length; i++) {
            if (telecom[i].system === item.system && telecom[i].id !== item.id) {
              telecom[i] = { ...telecom[i], rank: 2 };
            }
          }
        }

        await updatePatient({ telecom });
        setEditingContact(null);
        setShowForm(false);
        return;
      }

      // Adding new contact
      const telecom = patient.telecom ? [...patient.telecom] : [];
      let value = item.value;
      if (item.system === 'phone') {
        value = normalizePhoneNumber(value);
      }
      if (item.primary) {
        // Set all existing ranks to 2 (or greater than 1)
        for (let i = 0; i < telecom.length; i++) {
          if (telecom[i].system === item.system) {
            telecom[i] = { ...telecom[i], rank: 2 };
          }
        }
      }
      telecom.push({
        id: getUUID(),
        system: item.system,
        value,
        use: labelToUse(item.label),
        rank: item.primary ? 1 : undefined,
      });
      await updatePatient({ telecom });
      setShowForm(false);
    },
    [patient.telecom, updatePatient]
  );

  const handleEditContact = useCallback(
    (id: string, value: string, label: string, system: ContactSystem, primary: boolean) => {
      const normalizedLabel = label ? `${label.charAt(0).toUpperCase()}${label.slice(1).toLowerCase()}` : 'Mobile';
      const safeLabel = (['Mobile', 'Home', 'Work'] as ContactLabel[]).includes(normalizedLabel as ContactLabel)
        ? (normalizedLabel as ContactLabel)
        : 'Mobile';
      setEditingContact({ id, value, label: safeLabel, system, primary });
      setShowForm(true);
    },
    []
  );

  const handleCloseForm = useCallback(() => {
    setShowForm(false);
    setEditingContact(null);
  }, []);

  return (
    <InfoCard
      title="Contact information"
      addButtonAriaLabel="Add contact"
      defaultOpen={false}
      showAddForm={showForm}
      onShowAddFormChange={(show) => {
        if (!show) {
          handleCloseForm();
        } else {
          setEditingContact(null);
          setShowForm(true);
        }
      }}
      renderList={() => <UnifiedContactList items={items} onDeleteItem={handleDeleteItem} onEdit={handleEditContact} />}
      renderAddForm={() => (
        <AddContactPointForm
          onAdd={handleAddItem}
          onClose={handleCloseForm}
          initialValue={editingContact?.value}
          initialLabel={editingContact?.label}
          initialSystem={editingContact?.system}
          initialPrimary={editingContact?.primary}
          editingId={editingContact?.id}
        />
      )}
    />
  );
}
